import { useState } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import { StepShell } from 'components/Profile';
import { ProfileActions } from 'store/actions';
import Intro from './Intro';
import StepFrame from './Steps/Frame';
import Method from './Steps/Method';
import CardPhotos from './Steps/CardPhotos';
import PlanDetails from './Steps/PlanDetails';
import PolicyHolder from './Steps/PolicyHolder';
import Review from './Steps/Review';
import { DEFAULT_FLOW, INSURANCE_FLOWS, formatPayload, getCarrierOptions, getInitialValues, locateStep } from './helper';

const enhancer = connect(null, { ...ProfileActions });

const Form = ({ updateInsurance, insurance = null, practice = null, onSaved = null, inModal = false, onCancel = null }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext() || {};
  const initialData = insurance ?? sectionData?.insurance;
  const carrierOptions = getCarrierOptions(practice ?? sectionData?.practice);

  const [started, setStarted] = useState(false);
  const [flow, setFlow] = useState(DEFAULT_FLOW);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = INSURANCE_FLOWS[flow];
  const stepId = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const showIntro = () => setStarted(false);
  const exitWizard = onCancel ?? showIntro;

  const goTo = (index, nextFlow = flow) => {
    setFlow(nextFlow);
    setStepIndex(index);
  };

  const goBack = () => (stepIndex === 0 ? showIntro() : goTo(stepIndex - 1));

  const editStep = (targetId) => {
    const target = locateStep(targetId, flow);
    goTo(target.index, target.flow);
  };

  const save = async (values) => {
    try {
      const { data } = await updateInsurance(formatPayload(values));

      toast.success(values.active ? 'Insurance data saved successfully' : "You're marked as self-pay");

      if (onSaved)
        onSaved(data);
      else {
        mergeSection('insurance', data);
        goToNextStep('insurance');
      }

    } catch (error) {
      toast.error(error.errors || 'Failed to save insurance data. Please try again later.');
    }
  };

  const handleSubmit = async (values) => {
    if (!started || isLastStep) return save(values);

    setStepIndex(stepIndex + 1);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData, carrierOptions)}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, submitForm, isSubmitting }) => {
        const startWizard = () => {
          setFieldValue('active', true);
          setStarted(true);
        };

        const markSelfPay = async () => {
          await setFieldValue('active', false);
          submitForm();
        };

        return (
          <StepShell stepId="insurance" inModal={inModal} hideFooter>
            {started ? (
              <StepFrame
                stepId={stepId}
                current={stepIndex + 1}
                total={steps.length}
                onBack={goBack}
                onCancel={exitWizard}
                footer={stepId !== 'method'}
                submitLabel={isLastStep ? 'Save insurance' : 'Continue'}
                isSubmitting={isSubmitting}
              >
                {stepId === 'method' && <Method onChoose={(nextFlow) => goTo(1, nextFlow)} />}
                {stepId === 'card' && <CardPhotos onTypeInstead={() => goTo(1, 'typed')} />}
                {stepId === 'details' && <PlanDetails carrierOptions={carrierOptions} />}
                {stepId === 'holder' && (
                  <PolicyHolder
                    policyHolder={values.policy_holder}
                    onChange={(holder) => setFieldValue('policy_holder', holder)}
                  />
                )}
                {stepId === 'review' && <Review values={values} carrierOptions={carrierOptions} onEdit={editStep} />}
              </StepFrame>
            ) : (
              <Intro
                attributes={initialData}
                onAdd={startWizard}
                onSelfPay={markSelfPay}
                onCancel={onCancel}
                isSubmitting={isSubmitting}
              />
            )}
          </StepShell>
        );
      }}
    </Formik>
  );
};

export default enhancer(Form);
